import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { FILE_EXT_ICON_MAP, type FileIconName } from './constants';
import styles from './index.module.less';

const RADIUS = 5;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const LOADING_DASHOFFSET = CIRCUMFERENCE * 0.25;
const CDN_BASE = 'https://med-fe.cdn.bcebos.com/doctorclaw/filetype/';

interface InteractionInfo {
    url?: string;
    params?: Record<string, unknown>;
}

interface FileCardAction {
    interaction?: string;
    interactionInfo?: InteractionInfo;
}

export interface FileCardContent {
    fileName: string;
    fileTitle: string;
    fileSize: number;
    fileUrl: string;
    fileType?: string;
    action?: FileCardAction;
}

interface FileCardProps {
    content: FileCardContent;
}

const getFileIconSrc = (fileType?: string): string => {
    if (!fileType) return `${CDN_BASE}doudi.png`;
    const ext = fileType.toLowerCase();
    const iconName: FileIconName | undefined = FILE_EXT_ICON_MAP[ext as keyof typeof FILE_EXT_ICON_MAP];
    return `${CDN_BASE}${iconName ?? 'doudi'}.png`;
};

const formatFileSize = (bytes?: number) => {
    const safeBytes = Number(bytes) || 0;
    if (safeBytes >= 1024 * 1024) return `${(safeBytes / (1024 * 1024)).toFixed(1)}MB`;
    if (safeBytes >= 1024) return `${Math.round(safeBytes / 1024)}KB`;
    return `${safeBytes}B`;
};

const getActionUrl = (content: FileCardContent) => {
    return content.action?.interactionInfo?.url || content.fileUrl;
};

const FileCard: React.FC<FileCardProps> = ({ content }) => {
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        setIsDownloading(false);
    }, [content.fileName, content.fileUrl]);

    const iconSrc = useMemo(() => getFileIconSrc(content.fileType), [content.fileType]);
    const displayTitle = useMemo(() => {
        const title = content.fileTitle || content.fileName || '文件';
        const dotIndex = title.lastIndexOf('.');
        return dotIndex > 0 ? title.slice(0, dotIndex) : title;
    }, [content.fileName, content.fileTitle]);
    const fileSizeText = content.fileType
        ? `${content.fileType.toUpperCase()}，${formatFileSize(content.fileSize)}`
        : formatFileSize(content.fileSize);
    const actionText = '下载';

    const handleAction = useCallback(() => {
        if (isDownloading) return;
        const url = getActionUrl(content);
        if (!url) {
            message.warning('文件地址不存在');
            return;
        }

        setIsDownloading(true);
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => setIsDownloading(false), 1200);
    }, [content, isDownloading]);

    return (
        <div className={styles.fileCard}>
            <div className={styles.fileIcon}>
                <img src={iconSrc} className={styles.fileIconImg} alt="" />
            </div>
            <div className={styles.fileInfo}>
                <div className={styles.fileTitle} title={content.fileTitle || content.fileName}>
                    {displayTitle}
                </div>
                <div className={styles.fileSizeDesc}>{fileSizeText}</div>
            </div>
            <button className={styles.actionBtn} type="button" onClick={handleAction} disabled={isDownloading}>
                {isDownloading ? (
                    <span className={styles.progressRing}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                            <circle
                                cx="8"
                                cy="8"
                                r={RADIUS}
                                fill="none"
                                stroke="rgba(255,255,255,.35)"
                                strokeWidth="2"
                            />
                            <circle
                                cx="8"
                                cy="8"
                                r={RADIUS}
                                fill="none"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeDasharray={CIRCUMFERENCE}
                                strokeDashoffset={LOADING_DASHOFFSET}
                                className={styles.progressCircle}
                            />
                        </svg>
                    </span>
                ) : (
                    actionText
                )}
            </button>
        </div>
    );
};

export default memo(FileCard);
